"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

const RE_SINGLE_LINE_BREAK = /(?<!\n)\n(?!\n)/g;
const RE_ALL_LINE_BREAKS = /\n+/g;
const RE_MULTI_SPACES = /[ \t]{2,}/g;
const RE_PARA_BREAK = /\n{2,}/g;

type Mode = "single" | "all" | "paragraphs";

function removeLineBreaks(
  text: string,
  mode: Mode,
  replaceWithSpace: boolean
): string {
  if (text.length === 0) {
    return "";
  }
  const replacement = replaceWithSpace ? " " : "";

  if (mode === "single") {
    return text
      .replace(RE_SINGLE_LINE_BREAK, replacement)
      .replace(RE_MULTI_SPACES, " ");
  }

  if (mode === "all") {
    return text
      .replace(RE_ALL_LINE_BREAKS, replacement)
      .replace(RE_MULTI_SPACES, " ");
  }

  // paragraphs mode: remove single line breaks within paragraphs, keep paragraph breaks
  const paragraphs = text.split(RE_PARA_BREAK);
  return paragraphs
    .map((p) =>
      p
        .replace(RE_SINGLE_LINE_BREAK, replacement)
        .replace(RE_MULTI_SPACES, " ")
        .trim()
    )
    .filter((p) => p.length > 0)
    .join("\n\n");
}

function countLineBreaks(text: string): number {
  return (text.match(/\n/g) ?? []).length;
}

/** English strings. A translated content file overrides these through `ui`. */
export const REMOVE_LINE_BREAKS_STRINGS = {
  options: "Options",
  mode: "Mode",
  replaceLineBreaksWithA: "Replace line breaks with a space",
  inputText: "Input Text",
  pasteTextWithUnwantedLine: "Paste text with unwanted line breaks here...",
  lineBreaksDetected:
    "one:{count} line break detected|other:{count} line breaks detected",
  cleanedText: "Cleaned Text",
  lineBreaksRemoved:
    "one:{count} line break removed|other:{count} line breaks removed",
  copied: "Copied!",
  copyToClipboard: "Copy to Clipboard",
  modeSingleLineBreaksOnly: "Single line breaks only",
  modeAllLineBreaks: "All line breaks",
  modePreserveParagraphs: "Preserve paragraphs",
} as const;

export default function RemoveLineBreaksWidget() {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<Mode>("single");
  const [replaceWithSpace, setReplaceWithSpace] = useState(true);
  const [copied, setCopied] = useState(false);

  const output = removeLineBreaks(input, mode, replaceWithSpace);
  const inputBreaks = countLineBreaks(input);
  const outputBreaks = countLineBreaks(output);
  const removed = inputBreaks - outputBreaks;

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const locale = useToolLocale();
  const strings = useToolUi(REMOVE_LINE_BREAKS_STRINGS);
  const fmt = useToolNumber();
  const modeOptions: { value: Mode; label: string }[] = [
    { value: "single", label: strings.modeSingleLineBreaksOnly },
    { value: "all", label: strings.modeAllLineBreaks },
    { value: "paragraphs", label: strings.modePreserveParagraphs },
  ];

  return (
    <>
      <div className="space-y-4 rounded-lg border border-border bg-card p-5">
        <h2 className="font-medium text-foreground text-sm">
          {strings.options}
        </h2>
        <div className="flex flex-wrap gap-4">
          <fieldset className="space-y-2">
            <legend className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {strings.mode}
            </legend>
            <div className="flex flex-wrap gap-3">
              {modeOptions.map(({ value, label }) => (
                <label
                  className="flex cursor-pointer items-center gap-2 text-sm"
                  key={value}
                >
                  <input
                    checked={mode === value}
                    className="accent-primary"
                    name="mode"
                    onChange={() => setMode(value)}
                    type="radio"
                    value={value}
                  />
                  <span className="text-foreground">{label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            checked={replaceWithSpace}
            className="accent-primary"
            onChange={(e) => setReplaceWithSpace(e.target.checked)}
            type="checkbox"
          />
          <span className="text-foreground">
            {strings.replaceLineBreaksWithA}
          </span>
        </label>
      </div>
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="input-text"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="input-text"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.pasteTextWithUnwantedLine}
          value={input}
        />
        <p className="text-right text-muted-foreground text-xs">
          {tPlural(strings.lineBreaksDetected, locale, inputBreaks, {
            count: fmt(inputBreaks),
          })}
        </p>
      </div>
      {input.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="font-medium text-foreground text-sm">
                {strings.cleanedText}
              </p>
              <p className="text-muted-foreground text-xs">
                {tPlural(strings.lineBreaksRemoved, locale, removed, {
                  count: fmt(removed),
                })}
              </p>
            </div>
            <button
              className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground text-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              disabled={output.length === 0}
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyToClipboard}
            </button>
          </div>
          <textarea
            className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-muted/40 p-4 text-foreground text-sm leading-relaxed focus:outline-none"
            readOnly
            value={output}
          />
        </div>
      )}
    </>
  );
}
